import { Field } from '@amore/ui/components/field'
import { FormLayout } from '@amore/ui/components/form-layout'
import { Textarea } from '@amore/ui/components/textarea'
import { Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { newId } from '@/lib/format'
import { usePrototype } from '@/mock/store'
import type { CeremonyPreparation } from '@/mock/types'
import { ConfirmDialog, DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import {
  CeremonyFieldLabel,
  CeremonyPersonField,
  CeremonyTitleField,
  editorSections,
} from './editor-fields'
import { personKey } from './step-model'

export function CeremonyPreparationSheet({
  item,
  onClose,
}: {
  item?: CeremonyPreparation
  onClose: () => void
}) {
  const { canEdit, update } = usePrototype()
  const { showToast } = useToast()
  const [title, setTitle] = useState(item?.title ?? '')
  const [description, setDescription] = useState(item?.description ?? '')
  const [person, setPerson] = useState(item?.person ?? null)
  const [error, setError] = useState<string>()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const titleRef = useRef<HTMLTextAreaElement>(null)
  const form = useRef<HTMLFormElement>(null)
  const dirty =
    canEdit &&
    (title !== (item?.title ?? '') ||
      description !== (item?.description ?? '') ||
      (person ? personKey(person) : 'none') !== (item?.person ? personKey(item.person) : 'none'))
  const remove = () => {
    if (!canEdit || !item) return
    update((current) => ({
      ...current,
      ceremony: {
        ...current.ceremony,
        preparations: current.ceremony.preparations.filter((record) => record.id !== item.id),
      },
    }))
    showToast({ title: 'Domluva smazána', description: item.title })
    onClose()
  }
  return (
    <>
      <DetailSheet
        open
        eyebrow="Příprava místa"
        title={item?.title || 'Nová domluva'}
        titleContent={
          <CeremonyTitleField
            ref={titleRef}
            label="Název domluvy"
            value={title}
            onChange={(value) => {
              setTitle(value)
              setError(undefined)
            }}
            error={error}
            readOnly={!canEdit}
            autoFocus={!item && canEdit}
          />
        }
        dirty={dirty}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        primaryAction={
          canEdit ? { label: 'Uložit', onClick: () => form.current?.requestSubmit() } : undefined
        }
        destructiveAction={
          canEdit && item
            ? {
                label: 'Smazat domluvu',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setConfirmDelete(true),
              }
            : undefined
        }
      >
        <FormLayout
          ref={form}
          noValidate
          className={editorSections}
          onSubmit={(event) => {
            event.preventDefault()
            if (!canEdit) return
            if (!title.trim()) {
              setError('Doplňte název domluvy.')
              titleRef.current?.focus()
              return
            }
            const saved: CeremonyPreparation = {
              id: item?.id ?? newId('ceremony-preparation'),
              title: title.trim(),
              description: description.trim(),
              person,
            }
            update((current) => ({
              ...current,
              ceremony: {
                ...current.ceremony,
                preparations: item
                  ? current.ceremony.preparations.map((record) =>
                      record.id === item.id ? saved : record,
                    )
                  : [...current.ceremony.preparations, saved],
              },
            }))
            showToast({
              title: item ? 'Domluva uložena' : 'Domluva přidána',
              description: saved.title,
              tone: 'success',
            })
            onClose()
          }}
        >
          <CeremonyPersonField value={person} onChange={setPerson} disabled={!canEdit} />
          <Field
            controlId="ceremony-preparation-description"
            label={<CeremonyFieldLabel>Pokyny</CeremonyFieldLabel>}
          >
            <Textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              readOnly={!canEdit}
              maxLength={3000}
              placeholder="Co má být připravené a jak…"
              className="min-h-24 border-transparent bg-muted/60 hover:bg-muted focus:bg-card"
            />
          </Field>
        </FormLayout>
      </DetailSheet>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        heading="Smazat domluvu?"
        description={`Domluva „${item?.title ?? ''}“ se odebere z přípravy místa.`}
        keepLabel="Ponechat domluvu"
        confirmLabel="Smazat domluvu"
        onConfirm={remove}
      />
    </>
  )
}
